import React, { useEffect, useMemo, useState } from "react";
import { Card, Button, Skeleton, Tag } from "antd";
import { useNavigate } from "react-router-dom";
import i18n, { numberLocale } from "lang";
import { useApiService } from "services/ApiService";
import { useUserData } from "components/UserDataContext/UserDataContext";
import { APP_PREFIX_PATH } from "configs/AppConfig";
import { normalizeTitle } from "helper/FactoryRoles";

// Home page "Today" panel (UI and UX proposal, change 3: a home page per role).
// One small call (Dashboard/GetTodaySummary): the counts and first critical rows of My Dashboard, from the
// same procedures, so the figures always match it.
// Shop floor users (isLayman) see their department, other and regime jobs; everyone else sees
// approvals, critical components, work orders, regime jobs and demands. Each tile opens the
// screen behind it, and only when the user has that screen.
const TILES = {
  layman: [
    { status: "DepartmentJobs", label: "TodayDepartmentJobs", to: [["Dashboard", "view=departmentjobs"]] },
    { status: "OtherJobs", label: "TodayOtherJobs", to: [["Dashboard", "view=otherjobs"]] },
    { status: "RegimeJobs", label: "TodayRegimeJobs", to: [["ElectricalRegime"], ["Dashboard", "view=regimejobs"]] },
  ],
  manager: [
    { status: "PendingApprovals", label: "TodayPendingApprovals", warn: true, to: [["Dashboard", "view=pending"], ["ApprovalHistory"]] },
    { status: "Critical", label: "TodayCritical", alarm: true, to: [["Dashboard", "view=critical"], ["AssemblyWiseCriticalSpares"]] },
    { status: "WorkOrders", label: "TodayWorkOrders", to: [["Dashboard", "view=workorders"], ["WorkOrderPerformance"], ["DepartmentJobReport"]] },
    { status: "RegimeJobs", label: "TodayRegimeJobs", to: [["ElectricalRegime"], ["RegimeDashboard"], ["Dashboard", "view=regimejobs"], ["StopDayMaintenancePlanned", "tab=regime"]] },
    { status: "Demand", label: "TodayDemands", to: [["Dashboard", "view=demand"], ["DemandGenration"], ["DemandAndSpend"]] },
  ],
};

// When the user has none of the usual actions (log a stoppage, record a reading, open the
// dashboard), the first two of these screens they can open are offered instead, under their
// menu names.
const FALLBACK_ACTIONS = [
  "PreventiveMaintenanceDashboard",
  "PreventiveMaintenancePlanPerview",
  "StopDayMaintenancePlanned",
  "DemandGenration",
  "WarehouseSpares",
  "RequestNewJob",
  "ElectricalRegime",
];

// Colour of a component's status under "Critical now": how urgent the status is, not the
// fact that the component is critical (every row is).
const STATUS_TAG = {
  discard: { color: "red", key: "Discard" },
  repair: { color: "orange", key: "Repair" },
  inrepair: { color: "orange", key: "InRepair" },
  demand: { color: "gold", key: "Demand" },
  transfer: { color: "blue", key: "Transfer" },
  relocate: { color: "blue", key: "Transfer" },
  instorecommon: { color: "cyan", key: "InStoreCommon" },
  instorespecific: { color: "cyan", key: "InStoreSpecific" },
  inuse: { color: "green", key: "InUse" },
};
const statusTag = (status) => {
  const meta = STATUS_TAG[String(status || "").replace(/[^a-z]/gi, "").toLowerCase()];
  return meta ? { color: meta.color, text: i18n.t(meta.key) } : { color: "default", text: i18n.t(status) };
};

const greetingKey = () => {
  const h = new Date().getHours();
  return h < 12 ? "TodayGoodMorning" : h < 17 ? "TodayGoodAfternoon" : "TodayGoodEvening";
};

const TodayPanel = () => {
  const apiService = useApiService();
  const navigate = useNavigate();
  const userData = useUserData();
  const user = userData.users?.[0];
  const isLayman = !!user?.isLayman;
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);

  const screens = useMemo(() => {
    const byPath = new Map();
    (userData.factoryRoles || []).forEach((r) => {
      const key = (r.path || "").toLowerCase();
      if (key && r.isLeaveNode !== false && !byPath.has(key)) byPath.set(key, r);
    });
    return byPath;
  }, [userData.factoryRoles]);
  const has = (p) => screens.has(p.toLowerCase());
  const screenTitle = (p) => {
    const title = screens.get(p.toLowerCase())?.title || p;
    return i18n.t(normalizeTitle(title), { defaultValue: title });
  };
  const target = (to) => (to || []).find(([p]) => has(p));
  const href = ([p, query]) => `${APP_PREFIX_PATH}/${p}${query ? `?${query}` : ""}`;

  useEffect(() => {
    if (!user?.email) return;
    apiService(`dashboard/GetTodaySummary`, "GET")
      .then(async (r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        setData(await r.json());
      })
      .catch(() => setFailed(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.email, userData.factoryId]);

  if (failed) return null; // the rest of Home still works without this panel

  const counts = data?.dashboardCount || [];
  const count = (status) => counts.find((x) => x.status === status)?.quantity || 0;
  const tiles = TILES[isLayman ? "layman" : "manager"];
  const critical = (data?.criticalSummary || []).slice(0, 5);
  const anything = tiles.some((t) => count(t.status) > 0);
  const go = (dest) => dest && navigate(href(dest));
  const criticalTarget = target([["Dashboard", "view=critical"], ["AssemblyWiseCriticalSpares"]]);
  const rowTargetFor = (c) => criticalTarget || target(c.sectionName ? [[`${c.sectionName}_Spare`]] : []);
  const usual = has("Stoppages") || has("Telemetry") || has("Dashboard");
  const fallback = usual ? [] : FALLBACK_ACTIONS.filter((p) => has(p)).slice(0, 2);
  const onKey = (dest) => (e) => {
    if (dest && (e.key === "Enter" || e.key === " ")) {
      e.preventDefault();
      go(dest);
    }
  };

  return (
    <Card className="semmapp-today mb-3">
      <div className="semmapp-today-head">
        <div>
          <h3 className="mb-0">{i18n.t(greetingKey())}</h3>
          <div className="semmapp-today-sub">
            {data && !anything ? i18n.t("TodayNothing") : i18n.t("TodayIntro")}
          </div>
        </div>
        <div className="semmapp-today-actions">
          {has("Stoppages") && (
            <Button type="primary" onClick={() => navigate(`${APP_PREFIX_PATH}/Stoppages?quick=1`)}>
              {i18n.t("TodayLogStoppage")}
            </Button>
          )}
          {has("Telemetry") && (
            <Button onClick={() => navigate(`${APP_PREFIX_PATH}/Telemetry?quick=1`)}>{i18n.t("QuickReadingTitle")}</Button>
          )}
          {has("Dashboard") && <Button onClick={() => go(["Dashboard"])}>{i18n.t("TodayOpenDashboard")}</Button>}
          {fallback.map((p, i) => (
            <Button key={p} type={i === 0 ? "primary" : "default"} onClick={() => go([p])}>
              {screenTitle(p)}
            </Button>
          ))}
        </div>
      </div>

      {!data ? (
        <Skeleton active paragraph={{ rows: 2 }} title={false} />
      ) : (
        <>
          <div className="semmapp-today-tiles">
            {tiles.map((t) => {
              const n = count(t.status);
              const dest = target(t.to);
              return (
                <div
                  key={t.status}
                  className={`semmapp-today-tile${dest ? " is-link" : ""}${n > 0 && t.alarm ? " is-alarm" : ""}${n > 0 && t.warn ? " is-warn" : ""}`}
                  onClick={() => go(dest)}
                  onKeyDown={onKey(dest)}
                  role={dest ? "link" : undefined}
                  tabIndex={dest ? 0 : undefined}
                  title={dest ? screenTitle(dest[0]) : undefined}
                >
                  <div className="semmapp-num semmapp-today-value">{n.toLocaleString(numberLocale())}</div>
                  <div className="semmapp-today-label">{i18n.t(t.label)}</div>
                </div>
              );
            })}
          </div>

          {!isLayman && critical.length > 0 && (
            <div className="semmapp-today-list">
              <div className="semmapp-today-list-title">{i18n.t("TodayCriticalList")}</div>
              {critical.map((c, i) => {
                const tag = c.status ? statusTag(c.status) : null;
                const name = [c.spareDetailCode, c.spare].filter(Boolean).join(" · ");
                const where = [c.sectionName || c.departmentName, c.millName || c.assemblyName, c.subAssembly].filter(Boolean).join(" · ");
                const rowTarget = rowTargetFor(c);
                return (
                  <div
                    key={i}
                    className={`semmapp-today-row${rowTarget ? " is-link" : ""}`}
                    onClick={() => go(rowTarget)}
                    onKeyDown={onKey(rowTarget)}
                    role={rowTarget ? "link" : undefined}
                    tabIndex={rowTarget ? 0 : undefined}
                    title={rowTarget ? screenTitle(rowTarget[0]) : undefined}
                  >
                    <span className="semmapp-num">{name}</span>
                    <span>{where}</span>
                    {c.drawingNumber && <span className="semmapp-today-sub">{c.drawingNumber}</span>}
                    {tag && <Tag color={tag.color}>{tag.text}</Tag>}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </Card>
  );
};

export default TodayPanel;
